import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Grid,
  Typography,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import translationService, {
  Translation,
  TranslationInput,
} from "../../../api/services/translationService";
import { toast } from "react-toastify";

interface TranslationFormProps {
  open: boolean;
  onClose: () => void;
  translation?: Translation;
  onSuccess: () => void;
}

const TranslationForm: React.FC<TranslationFormProps> = ({
  open,
  onClose,
  translation,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<TranslationInput>({
    key: "",
    en: "",
    ar: "",
    app_type: "user",
    category: "general",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    if (translation) {
      setFormData({
        key: translation.key,
        en: translation.en,
        ar: translation.ar,
        app_type: translation.appType || "user",
        category: translation.category || "general",
        description: translation.description || "",
      });
    } else {
      setFormData({
        key: "",
        en: "",
        ar: "",
        app_type: "user",
        category: "general",
        description: "",
      });
    }
  }, [translation, open]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await translationService.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const handleChange =
    (field: keyof TranslationInput) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: event.target.value });
    };

  const handleSubmit = async () => {
    if (!formData.key || !formData.en || !formData.ar) {
      toast.error("Key, English, and Arabic translations are required");
      return;
    }

    setLoading(true);
    try {
      if (translation) {
        await translationService.update(translation.id, formData);
        toast.success("Translation updated successfully");
      } else {
        await translationService.create(formData);
        toast.success("Translation created successfully");
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Error saving translation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {translation ? "Edit Translation" : "Add Translation"}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Translation Key"
              value={formData.key}
              onChange={handleChange("key")}
              disabled={!!translation}
              placeholder="e.g., hello, welcome_message"
              helperText="Unique identifier for this translation (no spaces, use underscores)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="English Translation"
              value={formData.en}
              onChange={handleChange("en")}
              multiline
              rows={3}
              placeholder="Enter English translation"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Arabic Translation"
              value={formData.ar}
              onChange={handleChange("ar")}
              multiline
              rows={3}
              placeholder="Enter Arabic translation"
              InputProps={{
                style: { direction: "rtl" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="App Type"
              value={formData.app_type}
              onChange={handleChange("app_type")}
              disabled={!!translation}
              helperText={
                translation
                  ? "App type cannot be changed"
                  : "Select which app uses this translation"
              }
            >
              <MenuItem value="user">User App</MenuItem>
              <MenuItem value="clinician">Clinician App</MenuItem>
              <MenuItem value="shared">Shared (Both Apps)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Category"
              value={formData.category}
              onChange={handleChange("category")}
            >
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="auth">Authentication</MenuItem>
              <MenuItem value="session">Session</MenuItem>
              <MenuItem value="profile">Profile</MenuItem>
              <MenuItem value="booking">Booking</MenuItem>
              <MenuItem value="payment">Payment</MenuItem>
              <MenuItem value="error">Error Messages</MenuItem>
              <MenuItem value="success">Success Messages</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={handleChange("description")}
              multiline
              rows={2}
              placeholder="Optional: Add a description or context for this translation"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? "Saving..." : translation ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TranslationForm;
